import { adminApiAxios } from '@/api'
import type { DateTime } from 'luxon'

import { DATE_FORMATS_LUXON } from '../utils/dateHelpers'

import { fetchWrapper } from '../reducers/fetch'
import {
  JournalEntryLine,
  receiveJournalEntryLines,
} from '../reducers/admin/journalEntryLineReducer'
import { keyBy } from 'lodash'

// Admin Breakdowns

export interface AdminExpenses {
  expenses: Array<{
    transactionCategoryId: number
    sum: string
  }>
  sum: string | number
}
export const fetchAdminExpensesBreakdown = ({
  userId,
  startDate,
  endDate,
}: {
  userId: number | string
  startDate?: DateTime
  endDate?: DateTime
}) =>
  fetchWrapper({
    defaultErrorMessage: 'There was an error fetching the expenses breakdown.',
    defaultValue: {
      expenses: [],
      sum: 0,
    },
    fetchFunction: () =>
      adminApiAxios
        .get<AdminExpenses>(
          `/finances/api/v2/admin/reports/journal_entry_expenses_breakdown/${userId}`,
          {
            params: {
              startDate: startDate?.toFormat(DATE_FORMATS_LUXON.YEAR_MONTH_DAY),
              endDate: endDate?.toFormat(DATE_FORMATS_LUXON.YEAR_MONTH_DAY),
              year: !startDate && !endDate && 'all',
            },
          }
        )
        .then((json) => {
          return json.data
        }),
  })

export interface AdminProfits {
  profits: Array<{
    transactionCategoryId: number
    sum: string
  }>
  sum: string | number
}

export const fetchAdminProfitsBreakdown =
  ({
    userId,
    startDate,
    endDate,
  }: {
    userId: number | string
    startDate?: DateTime
    endDate?: DateTime
  }) =>
  () =>
    adminApiAxios
      .get<AdminProfits>(
        `/finances/api/v2/admin/reports/journal_entry_profits_breakdown/${userId}`,
        {
          params: {
            startDate: startDate?.toFormat(DATE_FORMATS_LUXON.YEAR_MONTH_DAY),
            endDate: endDate?.toFormat(DATE_FORMATS_LUXON.YEAR_MONTH_DAY),
            year: !startDate && !endDate && 'all',
          },
        }
      )
      .then((json) => {
        return json.data
      })
      .catch(() => ({
        profits: [],
        sum: 0,
      }))

export const fetchAdminUncategorizedBreakdown =
  ({
    userId,
    startDate,
    endDate,
  }: {
    userId: number
    startDate?: DateTime
    endDate?: DateTime
  }) =>
  () =>
    adminApiAxios
      .get<{ sum: number; transactionCategoryId: number }>(
        `/finances/api/v2/admin/reports/journal_entry_uncategorized_breakdown/${userId}`,
        {
          params: {
            startDate: startDate?.toFormat(DATE_FORMATS_LUXON.YEAR_MONTH_DAY),
            endDate: endDate?.toFormat(DATE_FORMATS_LUXON.YEAR_MONTH_DAY),
            year: !startDate && !endDate && 'all',
          },
        }
      )
      .then((json) => json.data)
      .catch(() => ({
        sum: 0,
        transactionCategoryId: null,
      }))

export const fetchAdminOtherBreakdown =
  ({
    userId,
    startDate,
    endDate,
  }: {
    userId: number
    startDate?: DateTime
    endDate?: DateTime
  }) =>
  () =>
    adminApiAxios
      .get<{
        otherExpenses: Array<{
          transactionCategoryId: number
          sum: string
        }>
        sum: string | number
      }>(
        `/finances/api/v2/admin/reports/journal_entry_other_breakdown/${userId}`,
        {
          params: {
            startDate: startDate?.toFormat(DATE_FORMATS_LUXON.YEAR_MONTH_DAY),
            endDate: endDate?.toFormat(DATE_FORMATS_LUXON.YEAR_MONTH_DAY),
            year: !startDate && !endDate && 'all',
          },
        }
      )
      .then((json) => {
        return json.data
      })
      .catch(() => ({
        otherExpenses: [],
        sum: 0,
      }))

export interface AdminProfitLossItem {
  name: string
  account_type: string
  sum: string
}

export const fetchAdminProfitAndLoss =
  ({
    userId,
    startDate,
    endDate,
  }: {
    userId: string | number
    startDate?: DateTime
    endDate?: DateTime
  }) =>
  () =>
    adminApiAxios
      .get<{
        profits: AdminProfitLossItem[]
        profitSum: string | number // Api returns 0 instead of "0"
        expenses: AdminProfitLossItem[]
        expensesSum: string | number
        other: AdminProfitLossItem[]
        otherSum: string | number
        uncategorizedSum: string | number
      }>(
        `/finances/api/v2/admin/reports/journal_entry_profit_and_loss/${userId}`,
        {
          params: {
            startDate: startDate?.toFormat(DATE_FORMATS_LUXON.YEAR_MONTH_DAY),
            endDate: endDate?.toFormat(DATE_FORMATS_LUXON.YEAR_MONTH_DAY),
            year: !startDate && !endDate && 'all',
          },
        }
      )
      .then((json) => json.data)
      .catch(() => ({
        profits: [],
        profitSum: 0,
        expenses: [],
        expensesSum: 0,
        other: [],
        otherSum: 0,
        uncategorizedSum: 0,
      }))

export const FETCH_JOURNAL_ENTRY_LINES_KEY = 'FETCH_JOURNAL_ENTRY_LINES_KEY'
export const fetchAdminJournalEntryLines = ({
  userId,
  startDate,
  endDate,
  transactionCategoryId,
  financialAccountId,
}: {
  userId: number
  startDate: DateTime
  endDate: DateTime
  transactionCategoryId?: number
  financialAccountId?: number
}) =>
  fetchWrapper({
    defaultErrorMessage: 'Error fetching journal entry lines',
    fetchKey: FETCH_JOURNAL_ENTRY_LINES_KEY,
    fetchFunction: async (dispatch) => {
      const json = await adminApiAxios.get<JournalEntryLine[]>(
        `/finances/api/v2/admin/reports/journal_entry_lines/${userId}`,
        {
          params: {
            startDate: startDate?.toFormat(DATE_FORMATS_LUXON.YEAR_MONTH_DAY),
            endDate: endDate?.toFormat(DATE_FORMATS_LUXON.YEAR_MONTH_DAY),
            transactionCategoryId,
            financialAccountId,
          },
        }
      )
      dispatch(receiveJournalEntryLines(keyBy(json.data, 'id')))
      return json.data
    },
  })
